import React, { useMemo, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { makeStyles } from "@material-ui/core/styles";
import LoadingState from "components/items/loading-state";
import { CloudFileUpload } from "helpers/icon-finder";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  dropZone: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#777",
    outline: "none",
    transition: "border .24s ease-in-out",
    justifyContent: "center",
    cursor: "pointer",
  },
  dropZoneFocused: {
    borderColor: "#2196f3",
  },
  dropZoneAccept: {
    borderColor: "#00e676",
  },
  dropZoneReject: {
    borderColor: "#ff1744",
  },
}));

function DropzoneAttachment({
  onFileUpload,
  disabled = false,
  loading,
  disabledLabel,
  label,
  helperText,
  extraParameters,
  isAcceptAllFormat = false,
  acceptFormat = {
    "application/pdf": [".pdf"],
  },
  // NEW: default to single file upload
  // If you want to allow multiple files, set multiple to true
  // and pass maxFiles to limit the number of files
  multiple = false,
  // Optional cap for multi-upload; ignored when multiple=false
  maxFiles,
}) {
  const classes = useStyles();

  const onDrop = useCallback(
    acceptedFiles => {
      if (!acceptedFiles?.length || !onFileUpload) return;

      // If multiple=true → pass the whole array; otherwise pass the first file
      const payload = multiple ? acceptedFiles : acceptedFiles[0];
      onFileUpload(payload, extraParameters);
    },
    [multiple, extraParameters, onFileUpload],
  );

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDrop,
    multiple,
    // Keep single-file behavior unless multiple=true. When multiple, allow unlimited
    // unless caller provides a maxFiles cap.
    maxFiles: multiple ? (maxFiles ?? undefined) : 1,
    accept: isAcceptAllFormat ? undefined : acceptFormat,
    disabled,
  });

  const dropZoneClass = useMemo(() => [
    classes.dropZone,
    isFocused && classes.dropZoneFocused,
    isDragAccept && classes.dropZoneAccept,
    isDragReject && classes.dropZoneReject,
  ].filter(Boolean).join(" "),
  [isFocused, isDragAccept, isDragReject, classes]);

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <div key={file.path}>
      {file.path} - {errors.map(e => e.message).join(", ")}
    </div>
  ));

  return (
    <div {...getRootProps({ className: dropZoneClass })}>
      <input {...getInputProps()} />
      {disabled && disabledLabel && <p>{disabledLabel}</p>}
      {loading && <LoadingState />}
      {!disabled && !loading && (
        <>
          <Typography variant="subtitle1">
            {label ?? (multiple
              ? "Drag and drop files here, or click to select"
              : "Drag and drop a file here, or click to select")}
          </Typography>
          {helperText && <Typography variant="caption">{helperText}</Typography>}
          <CloudFileUpload size={48} />
          {fileRejectionItems.length > 0
            && <Typography variant="caption" color="error">{fileRejectionItems}</Typography>}
        </>
      )}
    </div>
  );
}

export default DropzoneAttachment;
